import { useState, useCallback } from "react";
import { useAuth } from "react-oidc-context";
import { useAuthToken } from "../providers/auth-provider";
import { appConfig } from "@/config/app-config";
import { notifyPermissionDenied } from "@/lib/api-client";
const base = appConfig.apiUrlBase;
interface FetchResponse<T = any> {
  result: T | null;
  error: string | null;
  status?: number | null;
}
/**
 * Convierte un item de error (puede ser string u objeto) a texto legible.
 */
function formatErrorItem(item: unknown): string {
  if (!item) return "";
  if (typeof item === "string") return item;
  if (typeof item === "object" && item !== null) {
    const obj = item as Record<string, unknown>;
    // Formato ASP.NET: { description, message, reason, name }
    if (obj.description) return String(obj.description);
    if (obj.message) return String(obj.message);
    if (obj.reason) {
      const field = obj.name ? `${String(obj.name)}: ` : "";
      return `${field}${String(obj.reason)}`;
    }
    if (obj.name && obj.errorMessage) return `${String(obj.name)}: ${String(obj.errorMessage)}`;
    // Fallback: intentar serializar de forma legible
    try {
      const json = JSON.stringify(obj);
      return json.length < 300 ? json : "Error de validación. Revisa los campos del formulario.";
    } catch {
      return "Error de validación.";
    }
  }
  return String(item);
}

function extractErrorMessage(parsedBody: any, response: Response): string {
  if (!parsedBody) {
    return response.statusText || `HTTP ${response.status}`;
  }
  if (typeof parsedBody === "string") {
    return parsedBody;
  }
  if (parsedBody.message) return String(parsedBody.message);
  if (parsedBody.error) return String(parsedBody.error);
  if (parsedBody.detail) return String(parsedBody.detail);
  if (parsedBody.title) return String(parsedBody.title);
  if (Array.isArray(parsedBody.errors) && parsedBody.errors.length > 0) {
    return parsedBody.errors
      .slice(0, 5)
      .map(formatErrorItem)
      .filter(Boolean)
      .join(". ") || "Error en la operación.";
  }
  if (
    Array.isArray(parsedBody.validationErrors) &&
    parsedBody.validationErrors.length > 0
  ) {
    return parsedBody.validationErrors
      .slice(0, 5)
      .map(formatErrorItem)
      .filter(Boolean)
      .join(". ") || "Error de validación.";
  }
  return response.statusText || `HTTP ${response.status}`;
}
export function useFetch() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const token = useAuthToken();
  const auth = useAuth();
  const fetchData = useCallback(
    async (url: string, options: RequestInit = {}): Promise<FetchResponse> => {
      setLoading(true);
      setError(null);
      try {
        const isMultipart = options.body instanceof FormData;
        const defaultHeaders: HeadersInit = {
          Authorization: `Bearer ${token}`,
        };
        if (!isMultipart) {
          defaultHeaders["Content-Type"] = "application/json";
        }
        const headers = {
          ...defaultHeaders,
          ...(options.headers || {}),
        };
        const response = await fetch(`${base}${url}`, {
          ...options,
          headers,
        });
        // ✅ Detectar token expirado (401 Unauthorized)
        if (response.status === 401) {
          console.warn("Token expirado o inválido. Redirigiendo al login...");
          // Intentar renovar el token silenciosamente primero
          try {
            await auth.signinSilent();
            // Si el silent renew funciona, reintentar la petición
            const retryResponse = await fetch(`${base}${url}`, {
              ...options,
              headers: {
                ...headers,
                Authorization: `Bearer ${auth.user?.access_token}`,
              },
            });
            if (retryResponse.ok) {
              const contentType = retryResponse.headers.get("content-type") || "";
              const isJson = contentType.includes("application/json");
              const parsedBody = isJson ? await retryResponse.json() : await retryResponse.text();
              setLoading(false);
              return { result: parsedBody, error: null, status: retryResponse.status };
            }
          } catch {
            // Si el silent renew falla, redirigir al login
            console.warn("Silent renew falló. Redirigiendo al login...");
          }
          // Forzar re-login
          auth.signinRedirect({
            state: { returnUrl: window.location.pathname + window.location.search },
          });
          setLoading(false);
          return { result: null, error: "Sesión expirada. Redirigiendo al login...", status: 401 };
        }
        // ✅ Detectar permisos insuficientes (403 Forbidden)
        if (response.status === 403) {
          console.warn("[useFetch] Permiso denegado (403) para:", url);
          notifyPermissionDenied(`${base}${url}`);
        }

        const contentType = response.headers.get("content-type") || "";
        const isJson = contentType.includes("application/json");
        let parsedBody: any = null;
        try {
          parsedBody = isJson ? await response.json() : await response.text();
        } catch {
          parsedBody = null;
        }
        const businessError =
          parsedBody &&
          typeof parsedBody === "object" &&
          (parsedBody.success === false ||
            parsedBody.status === false ||
            parsedBody.statusCode >= 400);
        if (!response.ok || businessError) {
          const message = extractErrorMessage(parsedBody, response);
          setError(message);
          setLoading(false);
          return { result: parsedBody, error: message, status: response.status };
        }
        setLoading(false);
        return { result: parsedBody, error: null, status: response.status };
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : "Error en la petición";
        setError(errorMessage);
        setLoading(false);
        return { result: null, error: errorMessage, status: null };
      }
    },
    [token, auth]
  );
  return { fetchData, loading, error };
}
